/**
 * Client insights (owner, 1 Oct 2026): who comes to us and what for — clients advised under OUR
 * licence only, from the de-identified summary each save writes (shared/src/factFind/clientInsights.ts,
 * migration 0031). Bands and counts only; any group under ten people arrives as null and is shown as
 * "fewer than 10", so nothing here can be traced to a person.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getClientInsights, explainAdminError, type ClientInsightsReport } from '../../services/adminApi.ts';
import { INSIGHT_BAND_ORDER, INSIGHT_GOALS } from '../../../../shared/src/factFind/clientInsights.ts';
import { AdminMessage, SectionLabel } from './AdminShell.tsx';

const GOAL_LABELS: Record<string, string> = {
  home_care: 'Care at home',
  downsize_home: 'Downsize the home',
  retirement_village: 'Retirement village',
  granny_flat: 'Granny flat',
  aged_care: 'Residential aged care',
  meet_costs: 'Meet the costs',
  maximise_support: 'Maximise government support',
  preserve_assets: 'Preserve assets for the family',
  funding_guidance: 'Guidance on funding',
};

type Counts = Record<string, number | null> | undefined;

/** One distribution as horizontal bars, in a fixed order, with suppressed groups said plainly. */
const Distribution = ({ title, counts, order, labels }: { title: string; counts: Counts; order: readonly string[]; labels?: Record<string, string> }) => {
  const entries = order.filter((k) => counts && k in counts).map((k) => [k, counts![k] ?? null] as const);
  const max = Math.max(1, ...entries.map(([, v]) => v ?? 0));
  return (
    <div>
      <SectionLabel>{title}</SectionLabel>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing recorded yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {entries.map(([k, v]) => (
            <li key={k} className="grid grid-cols-[minmax(0,11rem)_1fr_4.5rem] items-center gap-3 text-sm">
              <span className="truncate text-slate-700">{labels?.[k] ?? k}</span>
              <span className="h-2 rounded-full bg-slate-100">
                {v !== null && <span className="block h-2 rounded-full bg-[#1a6fd4]" style={{ width: `${(v / max) * 100}%` }} />}
              </span>
              <span className={`text-right tabular-nums ${v === null ? 'text-xs text-slate-400' : 'font-semibold text-slate-900'}`}>
                {v === null ? 'fewer than 10' : v.toLocaleString('en-AU')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const InsightsPanel = () => {
  const [report, setReport] = useState<ClientInsightsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getClientInsights()
      .then((r) => { if (live) setReport(r); })
      .catch((err) => { if (live) setError(explainAdminError(err, 'Could not load client insights.')); });
    return () => { live = false; };
  }, []);

  if (error) return <AdminMessage tone="error">{error}</AdminMessage>;
  if (!report) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
        <Loader2 size={18} className="animate-spin" /> Loading insights…
      </div>
    );
  }

  const intro = (
    <p className="mb-8 max-w-3xl text-sm leading-relaxed text-slate-600">
      Clients advised under our licence: direct clients and clients of firms that are not licensed themselves. Built from a
      de-identified summary of each file — bands, never amounts, names or dates. Any group of fewer than {report.minGroup} people is
      hidden so no one can be picked out.
    </p>
  );

  if (report.tooFew) {
    return (
      <>
        {intro}
        <AdminMessage tone="info">
          {report.filesCounted} client file{report.filesCounted === 1 ? '' : 's'} so far. Insights appear once there are at least{' '}
          {report.minGroup}, so that no one can be picked out.
        </AdminMessage>
      </>
    );
  }

  // Goal mix within each wealth band, for the bands that have enough people to show.
  const byWealth = new Map<string, { goal: string; count: number | null }[]>();
  for (const row of report.goalsByNetWealth ?? []) {
    byWealth.set(row.band, [...(byWealth.get(row.band) ?? []), { goal: row.goal, count: row.count }]);
  }

  return (
    <div className="space-y-10">
      {intro}
      <div className="flex flex-wrap items-baseline gap-x-10 gap-y-2 border-b border-slate-200 pb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Client files counted</div>
          <div className="mt-1.5 text-4xl font-semibold tabular-nums tracking-tight text-slate-900">{report.filesCounted.toLocaleString('en-AU')}</div>
        </div>
        <div className="text-sm text-slate-600">
          {Object.entries(report.household ?? {}).map(([k, v]) => (
            <span key={k} className="mr-5">
              <span className="font-semibold text-slate-900">{v ?? 'fewer than 10'}</span> {k === 'couple' ? 'couples' : 'single'}
            </span>
          ))}
          {Object.entries(report.homeOwner ?? {}).map(([k, v]) => (
            <span key={k} className="mr-5">
              <span className="font-semibold text-slate-900">{v ?? 'fewer than 10'}</span> {k === 'owner' ? 'own their home' : "don't own a home"}
            </span>
          ))}
        </div>
      </div>

      <div className="grid gap-10 lg:grid-cols-2">
        <Distribution title="Goals chosen" counts={report.goals} order={INSIGHT_GOALS} labels={GOAL_LABELS} />
        <Distribution title="Age of the client" counts={report.ageBand} order={INSIGHT_BAND_ORDER.age} />
        <Distribution title="Net wealth, including the home" counts={report.netWealthBand} order={INSIGHT_BAND_ORDER.wealth} />
        <Distribution title="Home value" counts={report.homeValueBand} order={INSIGHT_BAND_ORDER.wealth} />
        <Distribution title="Super and account-based pensions" counts={report.superBand} order={INSIGHT_BAND_ORDER.super} />
        <Distribution title="Living expenses in retirement" counts={report.livingExpensesBand} order={INSIGHT_BAND_ORDER.expenses} />
      </div>

      <section>
        <SectionLabel>What people come to us for, by net wealth</SectionLabel>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-[0.08em] text-slate-500">
                <th className="px-5 py-3 font-semibold">Net wealth</th>
                {INSIGHT_GOALS.map((g) => (
                  <th key={g} className="px-2 py-3 text-right font-semibold">{GOAL_LABELS[g]}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {INSIGHT_BAND_ORDER.wealth.filter((b) => byWealth.has(b)).map((b) => (
                <tr key={b}>
                  <td className="whitespace-nowrap px-5 py-2.5 font-medium text-slate-800">{b}</td>
                  {INSIGHT_GOALS.map((g) => {
                    const cell = byWealth.get(b)?.find((x) => x.goal === g);
                    return (
                      <td key={g} className="px-2 py-2.5 text-right tabular-nums">
                        {!cell ? <span className="text-slate-300">—</span> : cell.count === null ? <span className="text-xs text-slate-400">&lt;{report.minGroup}</span> : <span className="text-slate-800">{cell.count}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-center text-xs text-slate-400">
        Our-licence clients only. Each file counts as it stood at its last save. Updated {new Date(report.generatedAt).toLocaleString('en-AU')}.
      </p>
    </div>
  );
};

export default InsightsPanel;
